'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useMDXComponent } from '@content-collections/mdx/react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@workspace/ui/components/accordion';
import {
  UnderlinedTabs,
  UnderlinedTabsContent,
  UnderlinedTabsList,
  UnderlinedTabsTrigger
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';

import { Callout } from '~/components/blog/callout';
import { CopyableCodeBlock } from '~/components/blog/copyable-code-block';
import { ExpandablePrompt } from '~/components/blog/expandable-prompt';
import { McpGuideNavigation } from '~/components/docs/mcp-guide-navigation';

type DocCardProps = {
  href: string;
  title: string;
  description: string;
  label?: string;
  featured?: boolean;
  wide?: boolean;
  className?: string;
};

type DocsScreenshotProps = {
  src: string;
  alt: string;
  caption: string;
};

type DocsVideoProps = {
  src: string;
  title: string;
  caption: string;
};

const components = {
  h1: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1
      className={cn(
        'font-heading mt-2 scroll-m-24 text-4xl font-bold',
        className
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2
      className={cn(
        'font-heading mb-4 mt-12 scroll-m-24 text-2xl font-semibold leading-8 tracking-tight',
        className
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3
      className={cn(
        'font-heading mt-8 scroll-m-24 text-xl font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4
      className={cn(
        'font-heading mt-8 scroll-m-24 text-lg font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h5
      className={cn(
        'mt-8 scroll-m-24 text-base font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h6
      className={cn(
        'mt-8 scroll-m-24 text-sm font-semibold tracking-tight',
        className
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }: React.HTMLAttributes<HTMLAnchorElement>) => (
    <a
      className={cn(
        'font-medium text-blue-500 underline underline-offset-4',
        className
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p
      className={cn('mt-6 leading-7', className)}
      {...props}
    />
  ),
  ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul
      className={cn('my-6 ml-6 list-disc', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol
      className={cn('my-6 ml-6 list-decimal', className)}
      {...props}
    />
  ),
  li: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <li
      className={cn('mt-2', className)}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <blockquote
      className={cn('mt-6 border-l-2 pl-6 italic', className)}
      {...props}
    />
  ),
  img: ({
    className,
    alt,
    ...props
  }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img
      className={cn('rounded-md', className)}
      alt={alt}
      {...props}
    />
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
    <hr
      className="my-4 md:my-8"
      {...props}
    />
  ),
  table: ({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-6 w-full overflow-y-auto rounded-none">
      <table
        className={cn('w-full overflow-hidden rounded-none', className)}
        {...props}
      />
    </div>
  ),
  tr: ({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr
      className={cn('m-0 border-t p-0', className)}
      {...props}
    />
  ),
  th: ({ className, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <th
      className={cn(
        'border px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <td
      className={cn(
        'border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),
  pre: CopyableCodeBlock,
  code: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <code
      className={cn(
        'relative rounded-sm bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm',
        className
      )}
      {...props}
    />
  ),
  Accordion,
  AccordionContent: ({
    className,
    ...props
  }: React.ComponentProps<typeof AccordionContent>) => (
    <AccordionContent
      className={cn('[&>p]:m-0', className)}
      {...props}
    />
  ),
  AccordionItem,
  AccordionTrigger,
  Callout,
  ExpandablePrompt,
  McpGuideNavigation,
  Image,
  Tabs: ({
    className,
    ...props
  }: React.ComponentProps<typeof UnderlinedTabs>) => (
    <UnderlinedTabs
      className={cn('relative mt-6 w-full', className)}
      {...props}
    />
  ),
  TabsList: ({
    className,
    ...props
  }: React.ComponentProps<typeof UnderlinedTabsList>) => (
    <UnderlinedTabsList
      className={cn('w-full border-b', className)}
      {...props}
    />
  ),
  TabsTrigger: ({
    className,
    ...props
  }: React.ComponentProps<typeof UnderlinedTabsTrigger>) => (
    <UnderlinedTabsTrigger
      className={cn('', className)}
      {...props}
    />
  ),
  TabsContent: ({
    className,
    ...props
  }: React.ComponentProps<typeof UnderlinedTabsContent>) => (
    <UnderlinedTabsContent
      className={cn('p-4', className)}
      {...props}
    />
  ),
  Link: ({ className, ...props }: React.ComponentProps<typeof Link>) => (
    <Link
      className={cn('font-medium underline underline-offset-4', className)}
      {...props}
    />
  ),
  DocCard: ({
    href,
    title,
    description,
    label,
    featured = false,
    wide = false,
    className
  }: DocCardProps) => (
    <Link
      href={href}
      className={cn(
        'group flex min-h-40 flex-col items-start rounded-xl border bg-card p-6 text-left text-card-foreground no-underline shadow-none transition-[transform,background-color,border-color] hover:-translate-y-0.5 hover:border-emerald-500/30 hover:bg-emerald-500/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transform-none',
        featured && 'min-h-0 border-emerald-500/20 bg-emerald-500/8 sm:p-8',
        wide && 'md:col-span-2',
        className
      )}
    >
      {label && (
        <span
          className={cn(
            'font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground',
            featured && 'text-emerald-700 dark:text-emerald-400'
          )}
        >
          {label}
        </span>
      )}
      <span
        className={cn(
          'mt-3 text-lg font-semibold tracking-tight text-foreground',
          featured && 'text-2xl'
        )}
      >
        {title}
      </span>
      <span className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
        {description}
      </span>
    </Link>
  ),
  DocsScreenshot: ({ src, alt, caption }: DocsScreenshotProps) => (
    <figure className="not-prose my-8 overflow-hidden rounded-xl border bg-card shadow-sm">
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        aria-label={`${alt} — open full-size image`}
        className="block overflow-hidden bg-[#f8f8f8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <Image
          src={src}
          alt={alt}
          width={1280}
          height={720}
          priority
          sizes="(min-width: 1280px) 720px, (min-width: 1024px) 60vw, 100vw"
          className="h-auto w-[105.27%] max-w-none -translate-x-[5%]"
        />
      </a>
      <figcaption className="flex flex-col gap-2 border-t px-4 py-3 text-sm leading-6 text-muted-foreground sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <span>
          {caption}{' '}
          <span className="text-muted-foreground/70">
            Example workspace data. Available actions may depend on your plan
            and connections.
          </span>
        </span>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open full size
        </a>
      </figcaption>
    </figure>
  ),
  DocsVideo: ({ src, title, caption }: DocsVideoProps) => (
    <figure className="not-prose my-8 overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="aspect-video w-full overflow-hidden bg-black">
        <iframe
          src={src.replace('/share/', '/embed/')}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="h-full w-full"
        />
      </div>
      <figcaption className="flex flex-col gap-2 border-t px-4 py-3 text-sm leading-6 text-muted-foreground sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <span>{caption}</span>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open in Loom
        </a>
      </figcaption>
    </figure>
  ),
  LinkedCard: ({ className, ...props }: React.ComponentProps<typeof Link>) => (
    <Link
      className={cn(
        'flex w-full flex-col items-center rounded-xl border bg-card p-6 text-card-foreground shadow transition-colors hover:bg-muted/50 sm:p-10',
        className
      )}
      {...props}
    />
  )
};

type MdxProps = {
  code: string;
  className?: string;
};

export function Mdx({ code, className }: MdxProps) {
  const Component = useMDXComponent(code);
  return (
    <div className={cn('mdx', className)}>
      <Component components={components} />
    </div>
  );
}
